import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    StepperMeasuresConstantType,
    StepperColorsConstantType,
    StepperDynamicStyleType,
    StepperSizeVariants,
    StepperMeasures,
    StepperColors
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getOpaqueColor
} from "../../utils";

export const STEPPER_TITLE_VARIANTS: StepperSizeVariants = {
    medium: "labelMediumSize",
    small: "labelSmallSize",
    large: "labelLargeSize"
};

export const STEPPER_TITLE_SELECTED_VARIANTS: StepperSizeVariants = {
    large: "titleMediumSize",
    medium: "titleSmallSize",
    small: "labelSmallSize"
};

export const STEPPER_MARKER_VARIANTS: StepperSizeVariants = {
    large: "labelMediumSize",
    medium: "labelSmallSize",
    small: "labelSmallSize"
};

export const STEPPER_MARKER_SELECTED_VARIANTS: StepperSizeVariants = {
    medium: "labelSmallSize",
    large: "titleSmallSize",
    small: "labelSmallSize"
};

export const STEPPER_DESCRIPTION_VARIANTS: StepperSizeVariants = {
    large: "bodyMediumSize",
    medium: "bodySmallSize",
    small: "bodySmallSize"
};

export const getStepperMeasures = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    touchTargets,
    orientation,
    pointerType,
    typography,
    iconSizes,
    isInSheet,
    borders,
    spaces,
    size
}: StepperMeasuresConstantType): StepperMeasures => {
    const height = controlHeights[size];

    const markerSize = Math.max(touchTargets.fine, height - shapeConfigs.chipHeightOffset);

    const getTouchHeight = () => {
        if(isInSheet) {
            return touchTargets.preferred;
        }

        if(pointerType !== "coarse") {
            return 0;
        }

        return orientation === "vertical" ? touchTargets.preferred : touchTargets.coarse;
    };

    return {
        inset: isInSheet ? spaces.spacingLg - spaces.spacingXs : (height - markerSize) / 2,
        lineHeight: typography[STEPPER_TITLE_VARIANTS[size]].lineHeight,
        markerSelectedVariant: STEPPER_MARKER_SELECTED_VARIANTS[size],
        titleSelectedVariant: STEPPER_TITLE_SELECTED_VARIANTS[size],
        descriptionVariant: STEPPER_DESCRIPTION_VARIANTS[size],
        rowHeight: Math.max(height, getTouchHeight()),
        markerVariant: STEPPER_MARKER_VARIANTS[size],
        titleVariant: STEPPER_TITLE_VARIANTS[size],
        controlPadding: controlPaddings.small,
        line: borders.indicator / 2,
        iconSize: iconSizes[size],
        markerSize,
        height
    };
};

export const getStepperColors = ({
    isHighlighted,
    status,
    colors
}: StepperColorsConstantType): StepperColors => {
    if(status === "completed") {
        return {
            markerBackgroundColor: colors.content.container.primary,
            markerBorderColor: colors.content.container.primary,
            markerContentColor: colors.content.icon.onPrimary,
            descriptionColor: colors.content.text.mid,
            titleColor: colors.content.text.high
        };
    }

    if(status === "error") {
        return {
            markerBackgroundColor: colors.content.container.danger,
            markerBorderColor: colors.content.container.danger,
            markerContentColor: colors.content.icon.onPrimary,
            descriptionColor: colors.content.text.danger,
            titleColor: colors.content.text.danger
        };
    }

    if(status === "disabled") {
        return {
            markerContentColor: colors.system.state.content.disabled.neutral,
            markerBorderColor: colors.system.state.border.disabled.neutral,
            titleColor: colors.system.state.content.disabled.neutral,
            markerBackgroundColor: getOpaqueColor({
                color: colors.system.state.overlay.disabled.neutral,
                surface: colors.system.default.background
            }),
            descriptionColor: colors.content.text.low
        };
    }

    if(status === "current") {
        return {
            markerBackgroundColor: colors.content.container.raised,
            markerBorderColor: colors.content.container.primary,
            markerContentColor: colors.content.text.emphasized,
            descriptionColor: colors.content.text.mid,
            titleColor: colors.content.text.high
        };
    }

    return {
        titleColor: isHighlighted ? colors.content.text.high : colors.content.text.mid,
        markerBackgroundColor: colors.content.container.raised,
        markerContentColor: colors.content.text.mid,
        markerBorderColor: colors.content.icon.low,
        descriptionColor: colors.content.text.mid
    };
};

const stylesheet = StyleSheet.create({
    container: {
        flexDirection: "column",
        minWidth: 0
    }
});

export const useStyles = ({
    spaces
}: StepperDynamicStyleType) => {
    const styles = {
        container: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
